import * as React from 'react'
import { IPet } from '../../interfaces/types.ts'
import { deviceFromPet } from '../../helpers/dataHelpers.ts'
import { isSleepFeatureEnabled, isLedFeatureEnabled } from '../../helpers/sleepHelper.ts'
import { findDevice, sendCommand } from '../../helpers/commandHelper.ts'
import { useSnapshot } from 'valtio'
import { dataState } from '../../store/dataState.ts'
import { historyState } from '../../store/historyState.ts'
import { setDrawerOpen } from '../../store/drawerState.ts'
import { useViewTransitionNavigate } from '../../hooks/useViewTransitionNavigate.ts'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlassLocation } from '@fortawesome/free-solid-svg-icons/faMagnifyingGlassLocation'
import { faClockRotateLeft } from '@fortawesome/free-solid-svg-icons/faClockRotateLeft'
import { faList } from '@fortawesome/free-solid-svg-icons/faList'
import { faHeartCircleCheck } from '@fortawesome/free-solid-svg-icons/faHeartCircleCheck'
import { faMoon } from '@fortawesome/free-solid-svg-icons/faMoon'
import { faLightbulb } from '@fortawesome/free-solid-svg-icons/faLightbulb'
import { ChangeCheckInRate } from '../common/ChangeCheckInRate/ChangeCheckInRate.tsx'
import { ScheduleSleep } from '../common/ScheduleSleep/ScheduleSleep.tsx'
import { RecentEventsPopup } from '../petDrawer/RecentEventsPopup.tsx'
import './Dashboard2.css'

interface Props {
    pet: IPet
    show: boolean
    disabled: boolean
}

// Shared button base — no DaisyUI btn, no default padding fighting us
const btnBase = 'w-full h-full rounded-lg border border-white bg-secondary text-white flex flex-col items-center justify-center gap-0.5 cursor-pointer hover:brightness-110 transition-[filter]'
const btnDisabled = 'w-full h-full rounded-lg border border-white bg-gray-400 text-white flex flex-col items-center justify-center gap-0.5 cursor-not-allowed opacity-60'

export const DeviceControls: React.FC<Props> = (props) => {
    const { pet, show, disabled } = props
    const { navigate } = useViewTransitionNavigate()

    const device = deviceFromPet(pet)
    const account = (useSnapshot(dataState) as typeof dataState).account
    const sleepEnabled = isSleepFeatureEnabled(account)
    const ledEnabled = isLedFeatureEnabled()
    const isSleeping = device?.status.sleeping === true
    const commandInProgress = !!device?.status.deviceCommandInProgressStatus
    const commandDisabled = disabled || isSleeping || commandInProgress
    const ledState = device?.status.ledState ?? 0
    const ledTip = ledState === 0 ? 'LED off' : ledState === 1 ? 'LED on' : ledState === 2 ? 'LED auto off (1 min)' : `unknown ${ledState}`
    const nextLedState = ledState === 0 ? 1 : ledState === 1 ? 2 : 0

    const [checkinOpen, setCheckinOpen] = React.useState(false)
    const [sleepOpen, setSleepOpen] = React.useState(false)
    const [eventsOpen, setEventsOpen] = React.useState(false)

    const showHistory = () => {
        if (window.location.pathname === '/history') {
            historyState.selectedPetId = pet._id
            setDrawerOpen(false)
        } else {
            navigate('/history', { state: { pet: { ...pet } } })
        }
    }

    return (
        <>
            <div
                id={'device-controls'}
                className={`${show ? 'show' : ''} popup bg-primary rounded-xl ml-1 p-1.5 grid grid-cols-2 gap-1.5 overflow-visible`}
                style={{ width: '12rem', height: '168px', gridTemplateRows: '2fr 1.5fr 1fr' }}
            >
                {/* Row 1: Find — full width, large icon, prominent */}
                <button
                    className={commandDisabled ? btnDisabled : btnBase}
                    style={{ gridColumn: '1 / -1' }}
                    disabled={commandDisabled}
                    onClick={() => device && findDevice(device)}
                >
                    <FontAwesomeIcon icon={faMagnifyingGlassLocation} size={'lg'} />
                    <span className={'text-xs font-semibold'}>Find</span>
                </button>

                {/* Row 2: History & Events */}
                <button className={btnBase} onClick={showHistory}>
                    <FontAwesomeIcon icon={faClockRotateLeft} />
                    <span className={'text-xs font-semibold'}>History</span>
                </button>
                <button className={btnBase} onClick={() => setEventsOpen(true)}>
                    <FontAwesomeIcon icon={faList} />
                    <span className={'text-xs font-semibold'}>Events</span>
                </button>

                {/* Row 3: Device commands — icon only, tooltip on hover */}
                <div
                    className={'grid gap-1.5 h-full'}
                    style={{ gridColumn: '1 / -1', gridTemplateColumns: `repeat(${[sleepEnabled, ledEnabled].filter(Boolean).length + 1}, 1fr)` }}
                >
                    <div className={'tooltip tooltip-left w-full h-full'} data-tip={'Check-in rate'}>
                        <button
                            className={btnBase}
                            onClick={() => setCheckinOpen(true)}
                        >
                            <FontAwesomeIcon icon={faHeartCircleCheck} />
                        </button>
                    </div>
                    {sleepEnabled && (
                        <div className={'tooltip tooltip-left w-full h-full'} data-tip={'Sleep'}>
                            <button
                                className={commandDisabled ? btnDisabled : btnBase}
                                disabled={commandDisabled}
                                onClick={() => setSleepOpen(true)}
                            >
                                <FontAwesomeIcon icon={faMoon} />
                            </button>
                        </div>
                    )}
                    {ledEnabled && (
                        <div className={'tooltip tooltip-left w-full h-full'} data-tip={ledTip}>
                            <button
                                className={commandDisabled ? btnDisabled : btnBase}
                                style={{ color: ledState === 1 ? 'hsl(var(--su))' : ledState === 2 ? 'hsl(var(--wa))' : 'white' }}
                                disabled={commandDisabled}
                                onClick={() => device && sendCommand('setLed', device.iccid, nextLedState)}
                            >
                                <FontAwesomeIcon icon={faLightbulb} />
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Modals rendered outside the panel so they don't affect layout */}
            {device && <ChangeCheckInRate tracker={device} open={checkinOpen} setOpen={setCheckinOpen} full disabled={commandDisabled} />}
            {device && sleepEnabled && <ScheduleSleep tracker={device} open={sleepOpen} setOpen={setSleepOpen} disabled={commandDisabled} />}
            <RecentEventsPopup
                pet={pet}
                open={eventsOpen}
                onClose={() => setEventsOpen(false)}
                onViewAll={() => navigate('/events', { state: { pet: { ...pet } } })}
                showExactTime
            />
        </>
    )
}
